import { Component } from '@theme/component';

/**
 * Certification chips of the product page (`blocks/pc-cert-chips.liquid`) and its certification icon cards
 * (`blocks/pc-pdp-certs.liquid`): any `[data-pc-cert-dialog]` element inside this component opens the dialog it points to.
 *
 * Every chip or card is a link to the ANSI guide (or a button) that works without JavaScript. This component turns the
 * click into a native modal: `dialog.showModal()` gives the focus trap, the Escape key and the focus return for
 * free. A click on the backdrop closes the dialog, and the focus goes back to the chip as a safety net for browsers
 * that do not restore it.
 *
 * All listeners live on the component element (capture phase for `close`, which does not bubble), so they stay
 * singular when the markup is replaced by a section re-render.
 *
 * @extends {Component}
 */
class PcCertChips extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /**
   * Chip that opened the dialog that is currently open.
   * @type {HTMLElement | null}
   */
  #opener = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('close', this.#onClose, { signal, capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
    this.#opener = null;
  }

  /**
   * Opens the dialog of a chip, or closes a dialog on a backdrop click.
   * @param {Event} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // The backdrop is part of the <dialog> element: a click on it (not on the panel) targets the dialog itself
    if (target instanceof HTMLDialogElement) {
      target.close();
      return;
    }

    const chip = target.closest('[data-pc-cert-dialog]');
    if (!(chip instanceof HTMLElement)) return;

    const dialog = this.querySelector(`#${CSS.escape(chip.dataset.pcCertDialog ?? '')}`);
    if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') return;

    // Keep the link as the fallback when the dialog cannot open
    event.preventDefault();
    this.#opener = chip;
    dialog.showModal();
  };

  /**
   * Returns the focus to the chip that opened the dialog.
   * @param {Event} event
   */
  #onClose = (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;

    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected) opener.focus();
  };
}

if (!customElements.get('pc-cert-chips')) {
  customElements.define('pc-cert-chips', PcCertChips);
}
